import Box from "@mui/material/Box"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import type { Row } from "@tanstack/react-table"
import { type ChangeEvent, type FC, useState } from "react"

import { GoodsReceiptFilterChips, GoodsReceiptFilterDrawer, GoodsReceiptsTable } from "../../../components"
import { CheckedInPurchaseOrdersTable } from "../../../components/GoodsReceiptsTable/CheckedInPurchaseOrdersTable"
import { useGoodsReceiptsTable } from "../../../hooks"
import type { GoodsReceiptActionState } from "../../../types"
import { GoodReceiptActionType } from "../../../types"

import WorkInProgressAlert from "@/components/common/Alerts/WorkInProgressAlert"
import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import { GoodsReceiptStatus } from "@/graphql/codegen/graphql"

const statuses = [GoodsReceiptStatus.CheckedIn]

export const CheckedInGoodsReceiptsContainer: FC = () => {
  const {
    goodsReceipts,
    isGoodsReceiptsFetching,
    searchQuery,
    warehouseAreaIds,
    warehouseOptions,
    isWarehousesFetching,
    expandedState,
    sortingState,
    tableContainerRef,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    handleExpandedChange,
    handleSortingChange,
    handleScroll,
  } = useGoodsReceiptsTable({
    statuses,
    routePath: "/app/warehouse-receiving",
  })

  const [actionState, setActionState] = useState<GoodsReceiptActionState>(null)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleSearchChange(event.target.value)
  }

  const handleFilterDrawerToggle = () => {
    setIsFilterDrawerOpen((prev) => !prev)
  }

  const handleEditGoodsReceipt = (goodsReceipt: GoodsReceipt) => {
    setActionState({ type: GoodReceiptActionType.Form, goodsReceipt })
  }

  const renderExpandedContent = (row: Row<GoodsReceipt>) => (
    <Box className='p-4'>
      <CheckedInPurchaseOrdersTable goodsReceipt={row.original} />
    </Box>
  )

  return (
    <Box className='flex h-full flex-col gap-y-6'>
      <Box className='flex items-center gap-x-4'>
        <SearchInput
          placeholder='Search by GR, Supplier, Tracking, Container or PO'
          defaultValue={searchQuery}
          onChange={handleSearchInputChange}
          className='w-full max-w-[524px]'
        />
        <Button appearance='outlined' startIcon={<FilterAltOutlined />} onClick={handleFilterDrawerToggle}>
          Filter
        </Button>
      </Box>

      {warehouseAreaIds.length > 0 && (
        <GoodsReceiptFilterChips
          warehouseAreaIds={warehouseAreaIds}
          warehouseOptions={warehouseOptions}
          onRemove={handleFilterRemove}
        />
      )}

      <GoodsReceiptsTable
        data={goodsReceipts}
        isLoading={isGoodsReceiptsFetching}
        expandedState={expandedState}
        sortingState={sortingState}
        status={GoodsReceiptStatus.CheckedIn}
        onExpandedChange={handleExpandedChange}
        onSortingChange={handleSortingChange}
        onScroll={handleScroll}
        tableContainerRef={tableContainerRef}
        onEdit={handleEditGoodsReceipt}
        renderExpandedContent={renderExpandedContent}
      />

      <GoodsReceiptFilterDrawer
        isOpen={isFilterDrawerOpen}
        warehouseAreaIds={warehouseAreaIds}
        warehouseOptions={warehouseOptions}
        isWarehousesFetching={isWarehousesFetching}
        onToggle={handleFilterDrawerToggle}
        onChange={handleFilterChange}
        onClear={handleFilterClear}
      />

      {actionState?.type === GoodReceiptActionType.Form && actionState.goodsReceipt && <WorkInProgressAlert />}
    </Box>
  )
}
